/* Refracts the live video through a fixed, seeded relief map. No external library. */
(() => {
  function createRelief() {
    const size = 512, height = new Float32Array(size * size);
    let seed = 7319;
    const random = () => { seed = (1664525 * seed + 1013904223) >>> 0; return seed / 4294967296; };
    for (const [cells, weight] of [[12, .17], [40, .34], [100, .35], [210, .14]]) {
      const grid = Float32Array.from({ length: cells * cells }, random);
      for (let y = 0; y < size; y++) {
        const py = y * cells / size, iy = Math.floor(py), dy = py - iy, sy = dy * dy * (3 - 2 * dy);
        for (let x = 0; x < size; x++) {
          const px = x * cells / size, ix = Math.floor(px), dx = px - ix, sx = dx * dx * (3 - 2 * dx);
          const a = grid[iy * cells + ix], b = grid[iy * cells + (ix + 1) % cells];
          const c = grid[((iy + 1) % cells) * cells + ix], d = grid[((iy + 1) % cells) * cells + (ix + 1) % cells];
          height[y * size + x] += ((a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy) * weight;
        }
      }
    }
    const pixels = new Uint8Array(size * size * 4);
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      const at = y * size + x, p = at * 4;
      const dx = height[y * size + (x + 1) % size] - height[y * size + (x + size - 1) % size];
      const dy = height[((y + 1) % size) * size + x] - height[((y + size - 1) % size) * size + x];
      pixels[p] = Math.max(0, Math.min(255, 128 + dx * 470));
      pixels[p + 1] = Math.max(0, Math.min(255, 128 + dy * 470));
      pixels[p + 2] = height[at] * 255; pixels[p + 3] = 255;
    }
    return { size, pixels };
  }
  const vertex = `attribute vec2 position; varying vec2 uv; void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
  const fragment = `precision mediump float;
    varying vec2 uv; uniform sampler2D room; uniform sampler2D relief;
    uniform vec2 viewport; uniform vec2 videoSize; uniform vec3 optics;
    vec3 sampleRoom(vec2 p){
      float screenAspect=viewport.x/viewport.y; float videoAspect=videoSize.x/videoSize.y;
      vec2 scale=vec2(min(screenAspect/videoAspect,1.),min(videoAspect/screenAspect,1.));
      return texture2D(room,clamp((p-.5)*scale+vec2(.46,.5),vec2(.003),vec2(.997))).rgb;
    }
    void main(){
      vec2 pixel=uv*viewport; vec4 surface=texture2D(relief,pixel/512.);
      vec2 normal=(surface.rg-.5)*2.;
      vec2 broad=(texture2D(relief,pixel/2048.+.37).rg-.5)*2.;
      vec2 refracted=uv+(normal+ broad*.75)*optics.x/viewport;
      vec2 blurSize=vec2(optics.y)/viewport;
      vec3 color=sampleRoom(refracted)*.16;
      color+=sampleRoom(refracted+blurSize*vec2(1.,0.))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(-1.,0.))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(0.,1.))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(0.,-1.))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(.71,.71))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(-.71,.71))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(.71,-.71))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(-.71,-.71))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(.38,.32))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(-.38,.32))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(.38,-.32))*.07;
      color+=sampleRoom(refracted+blurSize*vec2(-.38,-.32))*.07;
      float luminance=dot(color,vec3(.299,.587,.114));
      color=mix(vec3(luminance),color,.36)*1.11;
      color=mix(color,vec3(.78,.80,.775),.37);
      vec3 n=normalize(vec3(normal*1.8,1.));
      float highlight=pow(max(dot(n,normalize(vec3(-.5,.7,.8))),0.),9.);
      float shade=dot(n,normalize(vec3(-.5,.7,.8)))-.70;
      float reliefLight=(shade*.17+highlight*.26+(surface.b-.5)*.10)*optics.z;
      color+=reliefLight*(.32+luminance*.80);
      float vignette=1.-.065*length((uv-.5)*1.4);
      gl_FragColor=vec4(clamp(color*vignette,0.,1.),1.);
    }`;
  window.NinxsGlass = class {
    constructor(canvas, video) {
      this.canvas = canvas; this.video = video; this.active = false; this.frame = 0;
      this.options = [Number(canvas.dataset.refraction ?? 65), Number(canvas.dataset.blur ?? 20), Number(canvas.dataset.texture ?? 1.35)];
      const gl = canvas.getContext('webgl', { alpha: false, antialias: false, powerPreference: 'low-power' });
      if (!gl) return;
      this.gl = gl;
      try {
        const compile = (type, text) => { const s = gl.createShader(type); gl.shaderSource(s, text); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw Error(gl.getShaderInfoLog(s)); return s; };
        const program = gl.createProgram();
        gl.attachShader(program, compile(gl.VERTEX_SHADER, vertex)); gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragment)); gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw Error(gl.getProgramInfoLog(program));
        gl.useProgram(program); this.program = program;
        const buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,1,1]), gl.STATIC_DRAW);
        const at = gl.getAttribLocation(program, 'position'); gl.enableVertexAttribArray(at); gl.vertexAttribPointer(at, 2, gl.FLOAT, false, 0, 0);
        const texture = (unit, repeat) => {
          gl.activeTexture(gl.TEXTURE0 + unit); const t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE); return t;
        };
        this.videoTexture = texture(0, false); gl.uniform1i(gl.getUniformLocation(program, 'room'), 0);
        texture(1, true); const { size, pixels } = createRelief(); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels); gl.uniform1i(gl.getUniformLocation(program, 'relief'), 1);
        this.locations = Object.fromEntries(['viewport','videoSize','optics'].map(k => [k,gl.getUniformLocation(program,k)]));
        this.resize = new ResizeObserver(() => this.draw()); this.resize.observe(canvas);
        this.onData = () => this.draw(); video.addEventListener('loadeddata', this.onData); video.addEventListener('seeked', this.onData);
        canvas.addEventListener('webglcontextlost', event => { event.preventDefault(); this.setActive(false); canvas.parentElement.classList.remove('has-optics'); this.gl = null; });
        this.draw();
      } catch (error) { console.warn('Glass uses the CSS fallback:', error.message); this.gl = null; }
    }
    draw() {
      const gl = this.gl, video = this.video;
      if (!gl || video.readyState < 2) return;
      const rect = this.canvas.getBoundingClientRect();
      // A single CSS-pixel render keeps the surface detailed without a Retina-sized GPU workload.
      const width = Math.min(1920, Math.round(rect.width)), height = Math.round(rect.height * width / rect.width);
      if (!width || !height) return;
      if (this.canvas.width !== width || this.canvas.height !== height) { this.canvas.width = width; this.canvas.height = height; gl.viewport(0,0,width,height); }
      try {
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D,this.videoTexture); gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
        gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,video);
        gl.uniform2f(this.locations.viewport,rect.width,rect.height); gl.uniform2f(this.locations.videoSize,video.videoWidth,video.videoHeight); gl.uniform3fv(this.locations.optics,this.options);
        gl.drawArrays(gl.TRIANGLE_STRIP,0,4); this.canvas.parentElement.classList.add('has-optics');
      } catch (_) { this.canvas.parentElement.classList.remove('has-optics'); this.setActive(false); }
    }
    setActive(active) {
      this.active = active; cancelAnimationFrame(this.frame); this.frame = 0;
      if (!active) { this.draw(); return; }
      let previous = 0;
      const render = time => { if (!this.active) return; if (time - previous >= 1000/30) { this.draw(); previous = time; } this.frame = requestAnimationFrame(render); };
      this.frame = requestAnimationFrame(render);
    }
    setOptions(refraction, blur, texture) { this.options = [refraction, blur, texture]; this.draw(); }
    destroy() { this.setActive(false); this.resize?.disconnect(); this.video.removeEventListener('loadeddata',this.onData); this.video.removeEventListener('seeked',this.onData); this.gl?.getExtension('WEBGL_lose_context')?.loseContext(); }
  };
})();
